import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Label } from '@radix-ui/react-label';
import { cn } from '@utils/utils';

export interface DateTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  error?: string;
  className?: string;
  minDate?: Date;
  minTime?: Date;
  maxTime?: Date;
  maxDate?: Date;
  placeholder?: string;
  showTimeSelect?: boolean;
}

const parseDateTime = (dateTimeString?: string): Date | null => {
  if (!dateTimeString) return null;
  
  // Si contiene espacio, es formato con tiempo
  if (dateTimeString.includes(" ")) {
    const date = new Date(dateTimeString.replace(" ", "T"));
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Si es solo fecha (yyyy-MM-dd), crear la fecha en zona horaria local
  const [year, month, day] = dateTimeString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month es 0-indexado
  return isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (date: Date | null, showTimeSelect: boolean = true): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  if (!showTimeSelect) {
    return `${year}-${month}-${day}`;
  }
  
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const DateTimePicker = React.forwardRef<HTMLDivElement, DateTimePickerProps>(
  ({ value, onChange, disabled, label, error, className, minDate, minTime, maxTime, maxDate, placeholder, showTimeSelect = true }, ref) => {
    const selected = parseDateTime(value);

    return (
      <div className={cn("space-y-2 w-full", className)} ref={ref}>
        {label && (
          <Label
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </Label>
        )}
        <DatePicker
          selected={selected}
          onChange={(date: Date | null) => onChange(formatDateTime(date, showTimeSelect))}
          showTimeSelect={showTimeSelect}
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat={showTimeSelect ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"}
          placeholderText={placeholder || (showTimeSelect ? "Selecciona fecha y hora" : "Selecciona fecha")}
          className={cn(
            "w-full border rounded px-3 py-2 text-left bg-white",
            error ? "border-red-500" : "border-gray-300"
          )}
          disabled={disabled}
          autoComplete="off"
          minDate={minDate}
          maxDate={maxDate}
          minTime={minTime}
          maxTime={maxTime}
          popperPlacement="bottom-end"
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>
    );
  }
);
DateTimePicker.displayName = "DateTimePicker";

export { DateTimePicker }; 